import { Link } from "react-router-dom"
import { PUBLISH_SITES } from "../../api/config"
import type { Catalog, ProductGroup, WebsiteIdentity } from "../../api/types"
import type { GroupRights } from "../../auth/rights"
import { useAccess } from "../../auth/rights"
import { Note } from "../../components/Note"
import GroupSitePanel from "../../components/site/GroupSitePanel"
import CategoriesTab from "./CategoriesTab"

/**
 * Вкладка «Сайт»: группа на выбранном сайте прямо из карточки — видно, доехала
 * ли она, и её можно загрузить или убрать, не уходя на `/publish`.
 *
 * Панель та же, что на экране загрузки; разница только в том, что группа здесь
 * уже выбрана. Состав каталогов, назначение товаров и заведение новых сайтов
 * остаются на `/publish` — это операции не над группой, а над всем каталогом
 * или сайтом, и доступны только администратору.
 */
export default function SiteTab({
	group,
	languageId,
	rights,
	isForeign,
	productCount,
	sites,
	site,
	siteCatalog,
	onSite,
}: {
	group: ProductGroup
	languageId: number
	rights: GroupRights
	isForeign: boolean
	productCount?: number
	sites: WebsiteIdentity[]
	site: WebsiteIdentity | null
	siteCatalog: Catalog | null
	onSite: (id: number) => void
}) {
	const { canPublish, isAdmin } = useAccess()

	return (
		<div className="pub">
			<div className="pub-bar">
				<label className="pub-bar__field">
					<span className="pub-bar__label">Сайт</span>
					<select
						className="select"
						value={site?.id ?? ""}
						onChange={event => onSite(Number(event.target.value))}
					>
						{sites.map(item => (
							<option key={item.id} value={item.id}>
								{item.name}
							</option>
						))}
					</select>
				</label>

				<span className="pub-bar__spacer" />

				{/* экран `/publish` открыт только администратору — ссылку остальным
				    не показываем, она увела бы обратно к группам */}
				{isAdmin && (
					<Link to="/publish" className="btn">
						Каталоги и сайты →
					</Link>
				)}
			</div>

			{!site && (
				<Note tone="error">
					Ни один из сайтов загрузки не найден в справочнике `WebSites`:{" "}
					{PUBLISH_SITES.join(", ")}.
				</Note>
			)}
			{site && !siteCatalog && (
				<Note tone="error">
					В справочнике каталогов нет каталога с именем «{site.name}». Бэк ищет
					его именно по имени сайта, без него публикация не работает — правится
					на российской стороне.
				</Note>
			)}

			{site && siteCatalog && (
				<>
					{!canPublish && (
						<Note tone="hint">
							Загрузка на сайт доступна ролям «Контент-менеджер Латвии»,
							«Контент-менеджер Риги» и «Администратор». Вкладка открыта на
							чтение.
						</Note>
					)}

					<div className="panel">
						<div className="panel__head">
							<span>Группа на {site.name}</span>
							<span className="panel__spacer" />
							<span className="muted">без очистки таблиц</span>
						</div>
						<div className="panel__body">
							<GroupSitePanel
								group={group}
								site={site}
								siteCatalog={siteCatalog}
								productCount={productCount}
								disabled={!canPublish}
							/>
						</div>
					</div>

				</>
			)}

			{/* Категории живут в дереве ERP и от каталога сайта не зависят —
			    показываем их даже там, где публикация не настроена. */}
			<CategoriesTab
				group={group}
				languageId={languageId}
				rights={rights}
				isForeign={isForeign}
				siteName={site?.name}
			/>
		</div>
	)
}
